import { useDroppable } from '@dnd-kit/core';
import KanbanCard from './KanbanCard';

/**
 * KanbanColumn Component
 * Renders a Kanban column for a specific status, displaying its action items.
 * Supports pagination for the Closed column via hasMore and onLoadMore props.
 * 
 * @param {Object} props - Component props
 * @param {string} props.status - The status this column represents
 * @param {Array} props.items - Array of action items to display in this column
 * @param {Array} props.propertiesMDA - Array of properties for lookup
 * @param {Function} props.navigate - Navigation function
 * @param {boolean} props.hasMore - Whether there are more items to load (for pagination)
 * @param {number} props.totalItems - Total number of items in this status
 * @param {Function} props.onLoadMore - Function to call when loading more items
 * @param {Function} props.needsMyAttention - Function to check if item needs current user's attention
 * @param {Function} props.getItemIcon - Function to get icon for the item
 * @param {Function} props.getItemDisplayText - Function to get display text for the item
 * @param {Function} props.getUserDisplayName - Function to get display name for a user
 * @param {Function} props.formatCardDate - Function to format dates for display
 * @param {Function} props.getProjectName - Function to get project name from ID
 * @param {Array} props.allUsers - Array of all users for assignee lookup
 * @param {Function} props.isAssignedToMyDepartment - Function to check if item is assigned to user's department
 * @param {boolean} props.hideHeader - Whether to hide the column header (used in swimlane views where headers are shared)
 * @param {string} props.droppableId - Optional custom droppable ID (for unique IDs across swimlanes)
 */
const KanbanColumn = ({
    status,
    displayName,
    color,
    items,
    propertiesMDA,
    navigate,
    hasMore,
    totalItems,
    onLoadMore,
    needsMyAttention,
    getItemIcon,
    getItemDisplayText,
    getUserDisplayName,
    formatCardDate,
    getProjectName,
    allUsers,
    isAssignedToMyDepartment,
    hideHeader = false,
    droppableId
}) => {
    const { setNodeRef, isOver } = useDroppable({
        id: droppableId || status,
        data: { type: 'status-column', status }
    });
    
    return (
        <div
            ref={setNodeRef}
            className="kanban-column"
            style={{
                background: isOver ? "#e0f7fa" : undefined,
            }}
        >
            {!hideHeader && (
                <h3
                    className={`kanban-column-header kanban-header-${status.replace(/\s+/g, '').toLowerCase()}`}
                    style={color ? { background: color } : undefined}
                >
                    {displayName || status}
                    {totalItems > 0 && (
                        <span style={{ fontSize: '0.8em', fontWeight: 'normal', marginLeft: '8px' }}>
                            ({items.length} of {totalItems})
                        </span>
                    )}
                </h3>
            )}

            <div className="kanban-column-cards">
            {items.length === 0 ? (
                <p style={{ color: "#888" }}>No items</p>
            ) : (
                <>
                    {items.map(item => {
                        // Look up the property name from the pre-fetched properties list.
                        // Only show the label when the item has a propertyID set (non-zero)
                        // AND the property exists in the list.  An empty string is passed when
                        // either condition is false so the card does not show "Unknown Property"
                        // for items that have not yet been assigned to a property.
                        const property = item.propertyID
                            ? propertiesMDA?.find(p => p.propertyID === item.propertyID)
                            : undefined;
                        const propertyName = property ? property.propertyName : '';
                        return (
                            <KanbanCard
                                key={item.id}
                                item={item}
                                propertyName={propertyName}
                                onClick={() => navigate(`/action-items/${item.id}`)}
                                needsMyAttention={needsMyAttention}
                                getItemIcon={getItemIcon}
                                getItemDisplayText={getItemDisplayText}
                                getUserDisplayName={getUserDisplayName}
                                formatCardDate={formatCardDate}
                                getProjectName={getProjectName}
                                navigate={navigate}
                                allUsers={allUsers}
                                isAssignedToMyDepartment={isAssignedToMyDepartment}
                            />
                        );
                    })}

                    {/* Load More button for Closed column */}
                    {hasMore && onLoadMore && (
                        <button 
                            className="load-more-btn"
                            onClick={onLoadMore}
                            style={{
                                width: '100%',
                                padding: '10px',
                                marginTop: '10px',
                                background: '#f0f0f0',
                                border: '1px dashed #ccc',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                color: '#666',
                                fontSize: '0.9rem',
                                transition: 'all 0.2s ease'
                            }}
                        >
                            Load More ({totalItems - items.length} remaining)
                        </button>
                    )}
                </>
            )}
            </div>
        </div>
    );
};

export default KanbanColumn;
